import React, { useState, useEffect } from "react";
import MappingTopBox from "./CreatorTools/MappingTool/components/MappingTopBox";
import { toast } from "react-toastify";
import RestAPI from "../utils/restAPI";
import { ModuleTemplate, CreateModuleTemplatePayload } from "../shared/common/ModuleTypes";
import { ModuleType } from "@rebuild/shared";
import { useOrganizationSelect } from "../hooks/useOrganizationSelect";
import OrganizationSelector from "../components/ui/utils/OrganizationSelector";
import CreateEditModule from "../components/CreateEditModule";
import {
	getNoteTypeDisplayName,
	getEstimateTypeForNoteType,
	isReconstructionNoteType,
} from "./CreatorTools/MappingTool/utils/NoteType";
import { NOTE_TYPE_VALUES } from "@rebuild/shared";
import { NoteType } from "./CreatorTools/MappingTool/utils/NoteType";
import { FilterableSelect } from "./components/GenericInputComponents";

const ModuleConfigurationPage: React.FC = () => {
	const { organizations, selectedOrganization, setSelectedOrganization, loading } = useOrganizationSelect({ includeTestOrgs: true });

	const [selectedNoteType, setSelectedNoteType] = useState<NoteType>(NoteType.MITIGATION);
	const [selectedModuleType, setSelectedModuleType] = useState<ModuleType>(ModuleType.INITIAL);
	const [modules, setModules] = useState<ModuleTemplate[]>([]);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
	const [currentModule, setCurrentModule] = useState<Partial<ModuleTemplate>>({});

	const estimateType = getEstimateTypeForNoteType(selectedNoteType);
	const useNoteTypeFilter = isReconstructionNoteType(selectedNoteType);

	useEffect(() => {
		loadModules();
	}, [selectedOrganization, selectedNoteType, selectedModuleType]);

	const loadModules = async () => {
		setIsLoading(true);
		try {
			const params = new URLSearchParams();
			params.append("organizationId", selectedOrganization.organizationId);
			params.append("estimateType", estimateType);
			params.append("moduleType", selectedModuleType);
			if (useNoteTypeFilter) {
				params.append("noteType", selectedNoteType);
			}
			const data = await RestAPI.fetchRebuildAPI(`moduletemplates?${params.toString()}`, "GET", {}, "data", true, false);
			setModules(data || []);
		} catch (error) {
			console.error("Error loading modules:", error);
			toast.error("Failed to load modules");
		} finally {
			setIsLoading(false);
		}
	};

	const handleSave = async () => {
		try {
			const basePayload = {
				...currentModule,
				organizationId: selectedOrganization.organizationId,
				estimateType,
				moduleType: selectedModuleType,
				...(useNoteTypeFilter && { noteType: selectedNoteType }),
			};
			if (currentModule.id) {
				await RestAPI.fetchRebuildAPI(`moduletemplates/${currentModule.id}`, "PATCH", basePayload);
				toast.success("Module updated successfully");
			} else {
				await RestAPI.fetchRebuildAPI("moduletemplates", "POST", basePayload as CreateModuleTemplatePayload);
				toast.success("Module created successfully");
			}
			setIsModalOpen(false);
			loadModules();
		} catch (error) {
			console.error("Error saving module:", error);
			toast.error("Failed to save module");
		}
	};

	const handleDelete = async (id: string) => {
		if (!window.confirm("Are you sure you want to delete this module?")) return;
		try {
			await RestAPI.fetchRebuildAPI(`moduletemplates/${id}?organizationId=${selectedOrganization.organizationId}`, "DELETE");
			toast.success("Module deleted successfully");
			loadModules();
		} catch (error) {
			console.error("Error deleting module:", error);
			toast.error("Failed to delete module");
		}
	};
	const openModal = (module?: ModuleTemplate) => {
		if (module) {
			setCurrentModule({ ...module });
		} else {
			setCurrentModule({
				optional: false,
				order: modules.length + 1,
				level: "room",
				moduleType: selectedModuleType,
				title: "",
				mainQuestion: "",
				followUpPrompt: "",
				suggestions: [],
				...(useNoteTypeFilter && { noteType: selectedNoteType }),
			});
		}
		setIsModalOpen(true);
	};

	return (
		<div className="min-h-screen bg-gray-50">
			<MappingTopBox />
			<div className="ml-8 m4">
				{/* Filters Section */}
				<div className="mt-4 mb-8">
					<h1 className="text-3xl font-bold text-gray-800 mb-6">Module Configuration</h1>
					<div className="grid grid-cols-3 gap-8 mb-8">
						{/* Customer Select */}
						<OrganizationSelector organizations={organizations} selectedOrganization={selectedOrganization} onSelect={setSelectedOrganization} />
						{/* Estimate Type - same selector as CustomReesePromptsPage */}
						<div>
							<label className="block text-sm font-medium text-gray-700 mb-2">Estimate Type</label>
							<FilterableSelect
								value={selectedNoteType}
								onSelect={(newNoteType) => setSelectedNoteType((newNoteType as NoteType) ?? NoteType.MITIGATION)}
								options={NOTE_TYPE_VALUES}
							/>
						</div>
						{/* Module Type */}
						<div>
							<label className="block text-sm font-medium text-gray-700 mb-2">Module Type</label>
							<FilterableSelect
								value={selectedModuleType}
								onSelect={(newType) => setSelectedModuleType((newType as ModuleType) ?? ModuleType.INITIAL)}
								options={Object.values(ModuleType)}
							/>
						</div>
					</div>
				</div>

				{/* Modules List */}
				<div className="bg-white p-6 rounded-lg shadow mb-8">
					<div className="flex justify-between items-center mb-6">
						<h2 className="text-xl font-semibold text-gray-800">Modules</h2>
						<button onClick={() => openModal()} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
							Add Module
						</button>
					</div>

					{isLoading ? (
						<div className="flex justify-center py-8">
							<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
						</div>
					) : (
						<div className="space-y-4">
							{modules.map((module) => (
								<div key={module.id} className="border p-4 rounded-lg flex justify-between items-start hover:bg-gray-50">
									<div className="flex-1">
										<div className="flex items-center gap-4 mb-2">
											<h3 className="font-bold text-lg text-gray-800">{module.title}</h3>
											<span
												className={`text-xs px-2 py-1 rounded-full ${module.optional ? "bg-yellow-100 text-yellow-800" : "bg-green-100 text-green-800"}`}
											>
												{module.optional ? "Optional" : "Required"}
											</span>
											<span className="text-xs bg-gray-100 text-gray-800 px-2 py-1 rounded-full">{module.level} level</span>
											{module.noteType && (
												<span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
													{getNoteTypeDisplayName(module.noteType)}
												</span>
											)}
											<span className="text-xs text-gray-500">Order: {module.order}</span>
										</div>
										<p className="text-gray-700 font-medium mb-2">Q: {module.mainQuestion}</p>
										{module.suggestions && module.suggestions.length > 0 && (
											<div className="bg-gray-50 p-3 rounded-md">
												<h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Things to cover</h4>
												<ul className="space-y-1">
													{module.suggestions.map((suggestion, idx) => (
														<li key={idx} className="text-sm text-gray-600">
															<span className="font-medium">{suggestion.text}</span>
															{suggestion.subText && <span className="text-gray-500"> - {suggestion.subText}</span>}
														</li>
													))}
												</ul>
											</div>
										)}
									</div>
									<div className="flex gap-2 ml-4">
										<button
											onClick={() => openModal(module)}
											className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
											title="Edit"
										>
											<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
												<path
													strokeLinecap="round"
													strokeLinejoin="round"
													strokeWidth={2}
													d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
												/>
											</svg>
										</button>
										<button
											onClick={() => handleDelete(module.id)}
											className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
											title="Delete"
										>
											<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
												<path
													strokeLinecap="round"
													strokeLinejoin="round"
													strokeWidth={2}
													d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
												/>
											</svg>
										</button>
									</div>
								</div>
							))}
							{modules.length === 0 && (
								<div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
									<p className="text-gray-500">No modules found for this customer and estimate type.</p>
								</div>
							)}
						</div>
					)}
				</div>
			</div>

			{isModalOpen && (
				<CreateEditModule
					currentModule={currentModule}
					setCurrentModule={setCurrentModule}
					onClose={() => setIsModalOpen(false)}
					onSave={handleSave}
				/>
			)}
		</div>
	);
};

export default ModuleConfigurationPage;
